import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxV2,
  FilterType,
  Flex,
  Table,
  TableFilter,
  useFilters,
  useTable,
} from 'portal-commons';
import { Container } from '@material-ui/core';
import { isEqual } from 'lodash-es';

import { getAllCnps, getAllUsecaseTypes, getCampaignsByCsp } from '../apis';
import {
  AcceptCampaignModal,
  CampaignsActionDialog,
  CampaignsByCspsListingRow,
  CspCampaignDeclineModal,
} from '../components';
import { FilterToggleButton, Loader } from '../../../shared_elements';
import { globalGetService } from '../../../utils/globalApiServices';
import { CampaignListItem } from '@/apis/core/types';
import { Cnp } from '../../../types/user';
import { UseCaseType } from '../../../types/campaign';

import '../../../assets/styles/campaigns-by-csps-module.scss';
import { SelectItem } from '@/types';
import {
  getApprovedMnoOptions,
  getRejectionCategoryOptions,
} from '@/apis/services';
import { CAMPAIGN_REJECTION_ENABLED } from '@/constants';
import { CampaignActionType } from '../enums';
import { MAX_EVENT_RECORDS } from '@/features/events/constants';

const FILTER_CONFIG = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Type Campaign ID',
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    label: 'TCR Status',
  },
  sharingStatus: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Accepted', value: 'ACCEPTED' },
      { label: 'Pending', value: 'PENDING' },
    ],
    label: 'Acceptance Status',
    multiple: true,
    width: 260,
  },
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use Case',
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Type Brand ID',
  },
  brandUniversalEin: {
    type: FilterType.Text,
    label: 'Brand Universal EIN',
    placeholder: 'Type Brand Universal EIN',
  },
  cspName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Type CSP DBA Name',
    suggestions: [],
  },
  downstreamCnpUid: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Downstream CNP',
    multiple: true,
    width: 320,
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Type Brand DBA name',
    suggestions: [],
  },
  provisional: {
    type: FilterType.Dropdown,
    label: 'CNP Migration',
    placeholder: 'Select CNP Migration',
    options: [
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' },
    ],
  },
};

const headRows = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'brandName', label: 'BRAND DBA NAME', sortable: false },
  { id: 'cspName', label: 'CSP DBA NAME', sortable: false },
  { id: 'downstreamCnpName', label: 'DOWNSTREAM CNP', sortable: false },
  { id: 'usecase', label: 'USECASE', sortable: true },
  { id: 'sharedOnDate', label: 'SHARED ON', sortable: true },
  { id: 'status', label: 'TCR STATUS', sortable: false },
  { id: 'sharingStatus', label: 'ACCEPTANCE STATUS', sortable: false },
];

const fetchCspSuggestions = (query: Record<string, any> = {}) =>
  globalGetService('dca/csp/suggestions', { ...query, limit: 20 }).then(
    (response) => {
      if (response?.ok) {
        return response.data?.map((d: any) => d.cspName) ?? [];
      }
      return [];
    }
  );

const fetchBrandSuggestions = (query: Record<string, any> = {}) =>
  globalGetService('dca/brands/suggestions', {
    ...query,
    limit: 20,
    status: 'ACTIVE',
  }).then((response) => {
    if (response?.ok) {
      return response.data?.map((d: any) => d?.brandName) ?? [];
    }
    return [];
  });

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => fetchBrandSuggestions({ prefix: value }),
    },
    cspName: {
      minLength: 2,
      load: (value: string) => fetchCspSuggestions({ prefix: value }),
    },
  },
};

const CampaignsByCsps: React.FC = () => {
  const {
    appliedFilters,
    additionalFilters,
    candidateFilters,
    configs: filterConfigs,
    ...filter
  } = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });

  const fetchCampaigns = async () => {
    const queries = getQueries();
    const { provisional } = queries;

    if (provisional !== 'true' && provisional !== 'false') {
      return {
        data: table.tableData,
        total: table.total,
        rowsPerPage: table.pagination.rowsPerPage,
        page: table.pagination.page,
      };
    }
    const response = await getCampaignsByCsp({
      sharingStatus: 'PENDING,ACCEPTED',
      ...queries,
      channelId: '10DLC',
    });
    if (response) {
      return {
        data: response.records || [],
        rowsPerPage: response.recordsPerPage || 10,
        total: response.totalRecords || 0,
        page: response.page || 1,
      };
    }
    return {
      data: [],
      rowsPerPage: 10,
      total: 0,
      page: 1,
    };
  };

  const table = useTable(fetchCampaigns, {
    cappingTotal: 10000,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'sharedOnDate',
      ascendingOrder: false,
    },
  });

  const [actionType, setActionType] = useState<CampaignActionType>(
    CampaignActionType.None
  );
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignListItem>();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [rejectionCategories, setRejectionCategories] = useState<SelectItem[]>(
    []
  );
  const [approvedMnoOptions, setApprovedMnoOptions] = useState<SelectItem[]>(
    []
  );

  const fetchApprovedMnoOptions = async () => {
    const options = await getApprovedMnoOptions();
    setApprovedMnoOptions(options);
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchApprovedMnoOptions();
    CAMPAIGN_REJECTION_ENABLED && fetchRejectionCategories();
  }, []);

  const fetchRejectionCategories = async (): Promise<void> => {
    const options = await getRejectionCategoryOptions();
    setRejectionCategories(options);
  };

  useEffect(() => {
    const { provisional } = appliedFilters;
    if (provisional !== 'true' && provisional !== 'false') {
      filter.handleApply({
        ...appliedFilters,
        provisional: 'false',
      });
    }
  }, [appliedFilters.provisional]);

  useEffect(() => {
    const params = processFilterSpecialCase(candidateFilters);
    filter.handleEdit(params);
  }, [candidateFilters.sharingStatus, candidateFilters.status]);

  // handle the case when user manually changes the filter value in URL
  useEffect(() => {
    const params = processFilterSpecialCase(appliedFilters);
    if (!isEqual(params, appliedFilters)) {
      filter.handleApply(params);
    }
  }, [appliedFilters.sharingStatus, appliedFilters.status]);

  const getQueries = (): Record<string, any> => {
    return {
      ...table.filter,
      ...appliedFilters,
      ...additionalFilters,
    };
  };

  const fetchFilterOptions = async (): Promise<void> => {
    const cnps = await getAllCnps();
    const usecaseTypes = await getAllUsecaseTypes();
    filter.setOptions(
      'downstreamCnpUid',
      cnps.map((item: Cnp) => ({
        label: item.displayName,
        value: item.uid,
      }))
    );
    filter.setOptions(
      'usecase',
      usecaseTypes.map((item: UseCaseType) => ({
        label: item.displayName,
        value: item.id,
      }))
    );
  };

  const processFilterSpecialCase = (
    filterValues: Record<string, any> = {}
  ): Record<string, any> => {
    const isStatusExpired = filterValues.status === 'EXPIRED';
    const newValues = { ...filterValues };
    if (isStatusExpired && filterValues.sharingStatus !== 'ACCEPTED') {
      newValues.sharingStatus = 'ACCEPTED';
    }
    if (Boolean(filterConfigs.sharingStatus.disabled) !== isStatusExpired) {
      filter.setConfig('sharingStatus', {
        ...filterConfigs.sharingStatus,
        disabled: isStatusExpired,
      });
    }
    return newValues;
  };

  const handleActionClick = (
    type: 'accept' | 'reject',
    campaignData: CampaignListItem
  ) => {
    if (type === 'accept') {
      setActionType(CampaignActionType.Accept);
      setSelectedCampaign(campaignData);
    } else {
      setActionType(CampaignActionType.Reject);
      setSelectedCampaign(campaignData);
    }
  };

  const handleClose = (success: boolean): void => {
    if (success) {
      table.reload();
    }
    setActionType(CampaignActionType.None);
  };

  const toggleFilterVisibility = (): void => {
    setIsFilterVisible((prev) => !prev);
  };

  return (
    <section className="campaigns-by-csps-section" data-testid="campaignByCsps">
      <Container maxWidth={false} className="campaigns-by-csps-container">
        {table.pageLoading ? (
          <Loader />
        ) : (
          <>
            <Flex
              sx={{
                alignItems: 'center',
                mb: '8px',
                fontWeight: 600,
                fontSize: 'H600',
                lineHeight: '32px',
                color: 't.black80',
              }}
            >
              <span>
                {!table.pageLoading && table.total !== 0
                  ? `${table.total} 10DLC Campaigns Shared by CSPs`
                  : '10DLC Campaigns Shared by CSPs'}
              </span>
              <BoxV2
                sx={{
                  mt: '5px',
                  ml: '12px',
                  fontWeight: 400,
                  fontSize: 'H200',
                  lineHeight: '14px',
                  color: 't.black60',
                }}
              >
                Results capped at first{' '}
                {MAX_EVENT_RECORDS.toLocaleString('en-US')} records
              </BoxV2>
            </Flex>
            <Box className="page-actions">
              <FilterToggleButton
                visible={isFilterVisible}
                onClick={toggleFilterVisibility}
              />
            </Box>
            {isFilterVisible && (
              <TableFilter
                configs={filterConfigs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={filter.handleEdit}
                onAppliedValuesChange={filter.handleApply}
                className="filters"
                data-testid="campaignByCspsTableFilter"
              />
            )}
            <Table
              testId="campaignByCspsTable"
              loading={table.loading}
              handleChangePage={table.handleChangePage}
              createSortHandler={table.handleSorting}
              emptyState="no campaigns to view"
              filter={table.filter}
              headRows={headRows}
              tableData={table.tableData.map((record: any, index: number) => (
                <CampaignsByCspsListingRow
                  handleActionClick={handleActionClick}
                  key={record.uid}
                  campaign={record}
                />
              ))}
              pagination={table.pagination}
            />
          </>
        )}
      </Container>
      {selectedCampaign &&
        (CAMPAIGN_REJECTION_ENABLED ? (
          <>
            <AcceptCampaignModal
              open={actionType === CampaignActionType.Accept}
              primaryDca
              campaignUid={selectedCampaign.uid}
              options={approvedMnoOptions}
              onCancel={() => handleClose(false)}
              onSubmit={() => handleClose(true)}
            />
            <CspCampaignDeclineModal
              open={actionType === CampaignActionType.Reject}
              campaign={selectedCampaign}
              categories={rejectionCategories}
              onClose={handleClose}
            />
          </>
        ) : (
          <CampaignsActionDialog
            open={
              actionType === CampaignActionType.Accept ||
              actionType === CampaignActionType.Reject
            }
            handleClose={handleClose}
            type={actionType}
            campaignUid={selectedCampaign?.uid}
            brandName={selectedCampaign?.brandName}
            cspUid={selectedCampaign?.cspUid}
          />
        ))}
    </section>
  );
};

export default CampaignsByCsps;
